import { AfterViewInit, Component, effect, inject, signal, ViewChild } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Project, ProjectPhase } from '../../../core/models/project-model';
import { RouterLink } from '@angular/router';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { ProjectService } from '../../../core/projects/project';

@Component({
  selector: 'app-overview-projects',
  imports: [
    MatTableModule,
    RouterLink,
    MatSortModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule,
    MatButtonModule,
    MatCardModule,
  ],
  templateUrl: './overview-projects.html',
  styleUrl: './overview-projects.scss',
  standalone: true
})
export class OverviewProjects implements AfterViewInit {
  readonly projectService = inject(ProjectService);

  // Gemeinsames reaktives Signal aus ProjectService
  readonly projects = this.projectService.projects;

  // Filter State
  readonly activeFilter = signal<ProjectPhase | null>(null);
  readonly searchTerm = signal<string>('');

  // Table DataSource
  allProjects = new MatTableDataSource<Project>();

  constructor() {
    this.allProjects.sortingDataAccessor = (project: Project, sortHeaderId: string): string | number => {
      switch (sortHeaderId) {
        case 'contractNr':
          return project.contractNr?.toLowerCase() ?? '';
        case 'displayName':
          return project.displayName?.toLowerCase() ?? '';
        case 'phase':
          return project.phase ?? '';
        default:
          return (project as Record<string, any>)[sortHeaderId] ?? '';
      }
    };

    // Custom filterPredicate – muss VOR den effects gesetzt werden
    this.allProjects.filterPredicate = (project: Project, filter: string) => {
      const parsed = JSON.parse(filter) as { search: string; phase: ProjectPhase | null };

      // Phase-Filter
      if (parsed.phase !== null && project.phase !== parsed.phase) {
        return false;
      }

      // Text-Suche
      if (parsed.search) {
        const term = parsed.search.toLowerCase();
        return (
          project.contractNr?.toLowerCase().includes(term) ||
          project.displayName?.toLowerCase().includes(term)
        );
      }

      return true;
    };

    // Daten-Effect
    effect(() => {
      this.allProjects.data = this.projects();
      this.applyAllFilters();
    });

    // Filter-Effect
    effect(() => {
      this.activeFilter();
      this.searchTerm();
      this.applyAllFilters();
    });
  }

  displayedColumns = ['contractNr', 'displayName', 'phase', 'action'];

  @ViewChild(MatSort) sort!: MatSort;

  ngAfterViewInit() {
    this.allProjects.sort = this.sort;
  }

  applySearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchTerm.set(value);
  }

  onFilterChange(phase: ProjectPhase | null): void {
    this.activeFilter.set(phase);
  }

  private applyAllFilters(): void {
    const filterObj = {
      search: this.searchTerm(),
      phase: this.activeFilter(),
    };
    this.allProjects.filter = JSON.stringify(filterObj);
  }
}
